/**
 * YourClasses
 * Consolidated Track + Courses + Syllabus status component
 * Part of the Classes tab in the dashboard
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useProfileCourses,
  useLearningProfile,
  useAddCourse,
  useArchiveCourse,
  useArchivedCourses,
  useRestoreCourse,
  useUpdateProfile,
} from '@/hooks/use-learning-profile';
import { useSyllabi } from '@/hooks/use-syllabus';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  FileText,
  Check,
  Calendar,
  Archive,
  RotateCcw,
  GraduationCap,
  BookOpen,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getCurrentUser } from '@/lib/auth-token';
import { cn } from '@/lib/utils';
import { LEGAL_SUBJECTS, type CurrentStatus, type GoalMode } from '@/types/learning-profile';
import { slugToName, slugToUuid } from '@/lib/subject-mapping';

type CourseFilter = 'active' | 'archived' | 'all';

interface YourClassesProps {
  className?: string;
}

export function YourClasses({ className }: YourClassesProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [addCourseOpen, setAddCourseOpen] = useState(false);
  const [filter, setFilter] = useState<CourseFilter>('active');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customClassName, setCustomClassName] = useState('');
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);
  const { toast } = useToast();

  const { data: profile } = useLearningProfile();
  const { data: activeCourses = [], isLoading: coursesLoading } = useProfileCourses();
  const { data: archivedCourses = [] } = useArchivedCourses();
  const { data: syllabi = [] } = useSyllabi();

  const addCourse = useAddCourse();
  const archiveCourse = useArchiveCourse();
  const restoreCourse = useRestoreCourse();
  const updateProfile = useUpdateProfile();

  // Filter courses based on selected filter
  const courses =
    filter === 'active'
      ? activeCourses
      : filter === 'archived'
      ? archivedCourses
      : [...activeCourses, ...archivedCourses];

  // Handle current status change
  const handleStatusChange = async (value: string) => {
    try {
      await updateProfile.mutateAsync({ current_status: value as CurrentStatus });
      toast({
        title: 'Status updated',
        description: 'Your learning track has been saved.',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update status. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle goal mode change
  const handleGoalChange = async (value: string) => {
    try {
      await updateProfile.mutateAsync({ goal_mode: value as GoalMode });
      toast({
        title: 'Goal updated',
        description: 'Your learning goal has been saved.',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update goal. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Get syllabus for a course by matching subject
  const getSyllabusForCourse = (courseSubject: string) => {
    return syllabi.find((s) => {
      const courseName = s.course_name_extracted?.toLowerCase() || '';
      const subject = courseSubject.toLowerCase();
      // Match if course name contains the subject or vice versa
      return (
        courseName.includes(subject) ||
        subject.includes(courseName.replace(/\s+/g, '')) ||
        s.course_id === courseSubject
      );
    });
  };

  // Get exam date from syllabus
  const getExamInfo = (courseSubject: string) => {
    const syllabus = getSyllabusForCourse(courseSubject);
    if (!syllabus?.parsed_data?.exams?.length) return null;

    const now = new Date();
    const upcomingExams = syllabus.parsed_data.exams
      .filter((e) => e.date && new Date(e.date) > now)
      .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime());

    if (upcomingExams.length === 0) return null;

    const nextExam = upcomingExams[0];
    const daysUntil = Math.ceil(
      (new Date(nextExam.date!).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    return {
      type: nextExam.type || nextExam.name,
      date: nextExam.date,
      daysUntil,
    };
  };

  // Get subjects not yet added (check both active and archived)
  const allCourseSubjects = [...activeCourses, ...archivedCourses].map((c) => c.subject);
  const availableSubjects = LEGAL_SUBJECTS.filter(
    (subject) => !allCourseSubjects.includes(subject.value)
  );

  const handleAddCourse = (subjectValue: string) => {
    addCourse.mutate(
      { subject: subjectValue },
      {
        onSuccess: () => {
          setAddCourseOpen(false);
          // Switch to active filter to show newly added course
          setFilter('active');
        },
      }
    );
  };

  const handleArchiveCourse = (courseId: string) => {
    archiveCourse.mutate(courseId, {
      onSuccess: () => {
        toast({
          title: 'Class archived',
          description: 'You can restore it anytime from the Archived filter.',
        });
      },
      onError: () => {
        toast({
          title: 'Error',
          description: 'Failed to archive class. Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  const handleRestoreCourse = (courseId: string) => {
    restoreCourse.mutate(courseId, {
      onSuccess: () => {
        toast({
          title: 'Class restored',
          description: 'The class is now active again.',
        });
        // Switch to active filter to show restored course
        setFilter('active');
      },
      onError: () => {
        toast({
          title: 'Error',
          description: 'Failed to restore class. Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  // Handle creating a custom class (creates subject + course)
  const handleCreateCustomClass = async () => {
    if (!customClassName.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a class name.',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingCustom(true);
    try {
      // Get current user
      const user = getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      // Create the subject in subjects table
      const { data: subject, error: subjectError } = await supabase
        .from('subjects')
        .insert({
          name: customClassName.trim(),
          is_official: false,
          user_id: user.id,
        })
        .select()
        .single();

      if (subjectError) {
        // Check if it's a duplicate name error
        if (subjectError.code === '23505') {
          throw new Error('A class with this name already exists.');
        }
        throw subjectError;
      }

      // Generate a slug from the name for profile_courses
      const slug = customClassName.trim().toLowerCase().replace(/\s+/g, '_');

      // Add to profile_courses using the existing hook
      // Include subject_id to maintain FK relationship for name sync
      addCourse.mutate(
        { subject: slug, subject_id: subject.id, course_name: customClassName.trim() },
        {
          onSuccess: () => {
            // Invalidate subjects queries so flashcards picks up the new subject
            queryClient.invalidateQueries({ queryKey: ['subjects'] });

            toast({
              title: 'Custom class created!',
              description: `${customClassName.trim()} has been added to your classes.`,
            });

            // Reset state
            setCustomClassName('');
            setShowCustomInput(false);
            setAddCourseOpen(false);
            setFilter('active');
          },
          onError: (error) => {
            toast({
              title: 'Error adding class',
              description: error instanceof Error ? error.message : 'Failed to add class.',
              variant: 'destructive',
            });
          },
        }
      );
    } catch (error) {
      toast({
        title: 'Error creating class',
        description: error instanceof Error ? error.message : 'Something went wrong.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingCustom(false);
    }
  };

  // Reset custom input state when dialog closes
  const handleDialogChange = (open: boolean) => {
    setAddCourseOpen(open);
    if (!open) {
      setShowCustomInput(false);
      setCustomClassName('');
    }
  };

  const handleCourseClick = (courseSubject: string) => {
    const syllabus = getSyllabusForCourse(courseSubject);
    if (syllabus) {
      navigate(`/dashboard/classes/syllabi/view/${syllabus.id}`);
    }
  };

  // Check if a course is archived (for the "all" view)
  const isCourseArchived = (courseId: string) => {
    return archivedCourses.some((c) => c.id === courseId);
  };

  return (
    <TooltipProvider>
      <Card className={className}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-primary" />
              Your Classes
            </CardTitle>
            {/* Filter Pills */}
            <div className="flex rounded-full border p-0.5 bg-muted/50" role="tablist" aria-label="Filter classes">
              <button
                role="tab"
                aria-selected={filter === 'active'}
                onClick={() => setFilter('active')}
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded-full transition-colors',
                  filter === 'active'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Active
              </button>
              <button
                role="tab"
                aria-selected={filter === 'archived'}
                onClick={() => setFilter('archived')}
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded-full transition-colors',
                  filter === 'archived'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Archived
              </button>
              <button
                role="tab"
                aria-selected={filter === 'all'}
                onClick={() => setFilter('all')}
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded-full transition-colors',
                  filter === 'all'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                All
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Learning Track + Goal Mode - Side by Side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Learning Track Selector */}
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground" htmlFor="learning-track">
                What are you studying for?
              </label>
              <Select
                value={profile?.current_status || 'unknown'}
                onValueChange={handleStatusChange}
                disabled={updateProfile.isPending}
              >
                <SelectTrigger id="learning-track" className="w-full">
                  <SelectValue placeholder="Select your status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1L">1L - First year</SelectItem>
                  <SelectItem value="2L">2L - Second year</SelectItem>
                  <SelectItem value="3L">3L - Third year</SelectItem>
                  <SelectItem value="bar_prep">Bar Prep</SelectItem>
                  <SelectItem value="attorney">Attorney</SelectItem>
                  <SelectItem value="llm">LLM Student</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Goal Mode Selector */}
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground" htmlFor="goal-mode">
                What's your main goal?
              </label>
              <Select
                value={profile?.goal_mode || 'general'}
                onValueChange={handleGoalChange}
                disabled={updateProfile.isPending}
              >
                <SelectTrigger id="goal-mode" className="w-full">
                  <SelectValue placeholder="Select your goal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cold_calls">Cold Call Prep</SelectItem>
                  <SelectItem value="finals">Finals Prep</SelectItem>
                  <SelectItem value="bar">Bar Exam</SelectItem>
                  <SelectItem value="writing_improvement">Writing Improvement</SelectItem>
                  <SelectItem value="fundamentals">Fundamentals</SelectItem>
                  <SelectItem value="general">Just Exploring</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Course List */}
          <div className="space-y-2">
            {coursesLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : courses.length > 0 ? (
              courses.map((course) => {
                const syllabus = getSyllabusForCourse(course.subject);
                const examInfo = getExamInfo(course.subject);
                // For display: prefer linked_subject_name (FK-synced), then course_name, then slugToName (official), then raw subject
                // linked_subject_name reflects the current name in subjects table (updated when subject is edited)
                const subjectLabel = course.linked_subject_name || course.course_name || slugToName(course.subject) || course.subject;
                const isArchived = isCourseArchived(course.id);
                // Check if this is a custom class (no entry in SUBJECT_MAP)
                const isCustomClass = !slugToUuid(course.subject);

                return (
                  <div
                    key={course.id}
                    role={syllabus ? 'button' : undefined}
                    tabIndex={syllabus ? 0 : undefined}
                    className={cn(
                      'flex items-center justify-between p-3 rounded-lg border',
                      'bg-card transition-colors',
                      syllabus ? 'hover:bg-muted/50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2' : '',
                      isArchived && 'opacity-60'
                    )}
                    onClick={() => syllabus && handleCourseClick(course.subject)}
                    onKeyDown={(e) => {
                      if (syllabus && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleCourseClick(course.subject);
                      }
                    }}
                    aria-label={syllabus ? `View syllabus for ${subjectLabel}` : undefined}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{subjectLabel}</p>
                        {isCustomClass && !isArchived && (
                          <Badge variant="secondary" className="text-xs">
                            Custom
                          </Badge>
                        )}
                        {isArchived && (
                          <Badge variant="outline" className="text-xs">
                            Archived
                          </Badge>
                        )}
                      </div>
                      {syllabus ? (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-500" />
                          Syllabus uploaded
                          {syllabus.professor_name_extracted && (
                            <span> • Prof. {syllabus.professor_name_extracted}</span>
                          )}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">No syllabus</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {/* Exam countdown */}
                      {examInfo && !isArchived && (
                        <Badge
                          variant={
                            examInfo.daysUntil <= 7
                              ? 'destructive'
                              : examInfo.daysUntil <= 14
                              ? 'default'
                              : 'secondary'
                          }
                          className="text-xs"
                        >
                          <Calendar className="h-3 w-3 mr-1 hidden sm:inline" />
                          {examInfo.daysUntil}d
                        </Badge>
                      )}

                      {/* Study button - links to flashcards for this subject */}
                      {!isArchived && slugToUuid(course.subject) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-primary hover:text-primary/80"
                              onClick={() => navigate(`/flashcards/study?subject=${slugToUuid(course.subject)}`)}
                              aria-label={`Study ${subjectLabel} flashcards`}
                            >
                              <BookOpen className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Study {subjectLabel}</TooltipContent>
                        </Tooltip>
                      )}

                      {/* Syllabus action */}
                      {syllabus ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() =>
                                navigate(`/dashboard/classes/syllabi/view/${syllabus.id}`)
                              }
                              aria-label="View syllabus"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>View syllabus</TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => navigate('/dashboard/classes')}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Syllabus
                        </Button>
                      )}

                      {/* Archive/Restore course */}
                      {isArchived ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
                              onClick={() => handleRestoreCourse(course.id)}
                              aria-label="Restore class"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Restore class</TooltipContent>
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-amber-600"
                              onClick={() => handleArchiveCourse(course.id)}
                              aria-label="Archive class"
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Archive class</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                {filter === 'archived' ? (
                  <>
                    <p className="text-sm">No archived classes</p>
                    <p className="text-xs">Archived classes will appear here</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm">No classes added yet</p>
                    <p className="text-xs">Add your current classes to get started</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Add Class Button */}
          <Dialog open={addCourseOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Class
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {showCustomInput ? 'Create Custom Class' : 'Add a Class'}
                </DialogTitle>
                {showCustomInput && (
                  <DialogDescription>
                    Add a class that's not in our standard list
                  </DialogDescription>
                )}
              </DialogHeader>

              {showCustomInput ? (
                /* Custom class input form */
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="custom-class-name">Class Name</Label>
                    <Input
                      id="custom-class-name"
                      placeholder="e.g., Environmental Law, Securities Regulation"
                      value={customClassName}
                      onChange={(e) => setCustomClassName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isCreatingCustom) {
                          e.preventDefault();
                          handleCreateCustomClass();
                        }
                      }}
                      disabled={isCreatingCustom}
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setShowCustomInput(false);
                        setCustomClassName('');
                      }}
                      disabled={isCreatingCustom}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleCreateCustomClass}
                      disabled={isCreatingCustom || !customClassName.trim()}
                    >
                      {isCreatingCustom ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Class'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                /* Standard subject selection */
                <div className="space-y-4 py-4">
                  <div className="grid gap-2 max-h-[300px] overflow-y-auto">
                    {availableSubjects.length > 0 ? (
                      availableSubjects.map((subject) => (
                        <Button
                          key={subject.value}
                          variant="outline"
                          className="justify-start h-auto py-3"
                          onClick={() => handleAddCourse(subject.value)}
                          disabled={addCourse.isPending}
                        >
                          <p className="font-medium">{subject.label}</p>
                        </Button>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        All standard subjects have been added
                      </p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        or
                      </span>
                    </div>
                  </div>

                  {/* Create Custom Button */}
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => setShowCustomInput(true)}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Create Custom Class
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

export default YourClasses;
